import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getInventory, addInventoryItem, updateInventoryItem, deleteInventoryItem,
} from '../api';

const UNITS = ['pcs', 'kg', 'g', 'litre', 'ml', 'packet', 'box', 'dozen', 'bundle', 'pair'];
const CATEGORIES = [
  'Grains & Rice', 'Dal & Pulses', 'Oils & Ghee', 'Spices & Masalas',
  'Salt, Sugar & Sweeteners', 'Beverages', 'Dairy & Eggs', 'Dry Fruits & Nuts',
  'Snacks & Biscuits', 'Condiments & Sauces', 'Personal Care', 'Household Essentials',
  'Clothing', 'Electronics', 'Stationery', 'Fruits & Vegetables', 'General',
];

const EMPTY_FORM = {
  name: '', sku: '', category: 'General', quantity: '', unit: 'pcs',
  min_stock: '', cost_price: '', selling_price: '', expiry_date: '', notes: '',
};

// ── Status helpers ────────────────────────────────────────────────────────────
function statusOf(item) {
  const now = Date.now();
  if (item.quantity <= 0) return 'out';
  if (item.min_stock > 0 && item.quantity <= item.min_stock) return 'low';
  if (item.expiry_date) {
    const ms = new Date(item.expiry_date).getTime() - now;
    if (ms < 0) return 'expired';
    if (ms < 7 * 864e5) return 'expiring';
  }
  if (item.last_restocked) {
    const daysSince = (now - new Date(item.last_restocked).getTime()) / 864e5;
    if (daysSince > 45) return 'slow';
  }
  return 'ok';
}

const STATUS_META = {
  out:      { label: 'Out of Stock',   color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   dot: '🔴' },
  low:      { label: 'Low Stock',      color: '#f97316', bg: 'rgba(249,115,22,0.12)',  dot: '🟠' },
  expired:  { label: 'Expired',        color: '#7c3aed', bg: 'rgba(124,58,237,0.12)', dot: '🟣' },
  expiring: { label: 'Expiring Soon',  color: '#eab308', bg: 'rgba(234,179,8,0.12)',  dot: '🟡' },
  slow:     { label: 'Slow Moving',    color: '#64748b', bg: 'rgba(100,116,139,0.1)', dot: '⚪' },
  ok:       { label: 'In Stock',       color: '#22c55e', bg: 'rgba(34,197,94,0.10)',  dot: '🟢' },
};

// ── Barcode Scanner overlay ───────────────────────────────────────────────────
function BarcodeScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const rafRef   = useRef(null);
  const [err, setErr] = useState('');
  const supported = 'BarcodeDetector' in window;

  useEffect(() => {
    if (!supported) return;
    let stream;
    const detector = new window.BarcodeDetector({
      formats: ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'itf'],
    });

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        stream = s;
        videoRef.current.srcObject = s;
        videoRef.current.play();

        const tick = async () => {
          if (!videoRef.current) return;
          try {
            const found = await detector.detect(videoRef.current);
            if (found.length > 0) { onScan(found[0].rawValue); return; }
          } catch {}
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      })
      .catch(() => setErr('Camera access denied. Allow camera permission and try again.'));

    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onScan, supported]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
      zIndex: 9999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      {!supported ? (
        <div style={{ color: '#fff', textAlign: 'center', padding: 24, maxWidth: 320 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📷</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Scanner not supported</div>
          <div style={{ fontSize: 13, color: '#aaa', marginBottom: 20 }}>
            Use Chrome on Android/Desktop for barcode scanning. Enter barcode manually below.
          </div>
          <button onClick={onClose} style={closeBtnStyle}>Close</button>
        </div>
      ) : err ? (
        <div style={{ color: '#fff', textAlign: 'center', padding: 24, maxWidth: 320 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{err}</div>
          <button onClick={onClose} style={closeBtnStyle}>Close</button>
        </div>
      ) : (
        <>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Point camera at barcode / QR code</div>
          <div style={{ position: 'relative', width: 280, height: 280 }}>
            <video ref={videoRef} style={{ width: 280, height: 280, objectFit: 'cover', borderRadius: 12 }} muted playsInline />
            {/* Scanner crosshair */}
            <div style={{
              position: 'absolute', inset: 0, border: '2px solid #FF6B35', borderRadius: 12,
              boxShadow: 'inset 0 0 0 40px rgba(0,0,0,0.3)',
            }} />
            <div style={{ position: 'absolute', top: 20, left: 20, width: 30, height: 30, borderTop: '3px solid #FF6B35', borderLeft: '3px solid #FF6B35' }} />
            <div style={{ position: 'absolute', top: 20, right: 20, width: 30, height: 30, borderTop: '3px solid #FF6B35', borderRight: '3px solid #FF6B35' }} />
            <div style={{ position: 'absolute', bottom: 20, left: 20, width: 30, height: 30, borderBottom: '3px solid #FF6B35', borderLeft: '3px solid #FF6B35' }} />
            <div style={{ position: 'absolute', bottom: 20, right: 20, width: 30, height: 30, borderBottom: '3px solid #FF6B35', borderRight: '3px solid #FF6B35' }} />
          </div>
          <button onClick={onClose} style={closeBtnStyle}>Cancel</button>
        </>
      )}
    </div>
  );
}
const closeBtnStyle = {
  padding: '10px 28px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

// ── Item form modal ───────────────────────────────────────────────────────────
function ItemModal({ initial, onSave, onClose }) {
  const [form, setForm]   = useState(initial || EMPTY_FORM);
  const [busy, setBusy]   = useState(false);
  const [err,  setErr]    = useState('');
  const [scan, setScan]   = useState(false);

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { setErr('Product name is required.'); return; }
    setBusy(true); setErr('');
    try {
      await onSave({
        ...form,
        quantity:      parseFloat(form.quantity)      || 0,
        min_stock:     parseFloat(form.min_stock)     || 0,
        cost_price:    parseFloat(form.cost_price)    || 0,
        selling_price: parseFloat(form.selling_price) || 0,
      });
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed');
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const row = (label, child) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
      {child}
    </div>
  );

  return (
    <>
      {scan && <BarcodeScanner onScan={(v) => { setForm((p) => ({ ...p, sku: v })); setScan(false); }} onClose={() => setScan(false)} />}
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: '24px 20px',
          width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{initial ? 'Edit Item' : 'Add Inventory Item'}</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#666' }}>×</button>
          </div>

          {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#dc2626', marginBottom: 14 }}>{err}</div>}

          {row('Product Name *', <input style={inputStyle} placeholder="e.g. Basmati Rice 1kg" value={form.name} onChange={f('name')} />)}

          {row('Barcode / SKU',
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Scan or type barcode" value={form.sku} onChange={f('sku')} />
              <button type="button" onClick={() => setScan(true)}
                style={{ padding: '9px 14px', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 13 }}>
                📷 Scan
              </button>
            </div>
          )}

          {row('Category',
            <select style={inputStyle} value={form.category} onChange={f('category')}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>{row('Current Quantity', <input style={inputStyle} type="number" min="0" step="0.01" placeholder="0" value={form.quantity} onChange={f('quantity')} />)}</div>
            <div>{row('Unit',
              <select style={inputStyle} value={form.unit} onChange={f('unit')}>
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            )}</div>
          </div>

          {row('Min Stock Alert (alert when qty falls below this)',
            <input style={inputStyle} type="number" min="0" step="0.01" placeholder="e.g. 5" value={form.min_stock} onChange={f('min_stock')} />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>{row('Cost Price ₹', <input style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" value={form.cost_price} onChange={f('cost_price')} />)}</div>
            <div>{row('Selling Price ₹', <input style={inputStyle} type="number" min="0" step="0.01" placeholder="0.00" value={form.selling_price} onChange={f('selling_price')} />)}</div>
          </div>

          {row('Expiry Date (optional)',
            <input style={inputStyle} type="date" value={form.expiry_date} onChange={f('expiry_date')} />
          )}

          {row('Notes (optional)', <input style={inputStyle} placeholder="Any notes" value={form.notes} onChange={f('notes')} />)}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={submit} disabled={busy}
              style={{ flex: 1, padding: '12px', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Saving…' : (initial ? 'Save Changes' : 'Add Item')}
            </button>
            <button onClick={onClose}
              style={{ padding: '12px 20px', background: '#f3f4f6', color: '#333', border: 'none', borderRadius: 10, fontSize: 15, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main InventoryTab ─────────────────────────────────────────────────────────
export default function InventoryTab() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');
  const [modal,    setModal]    = useState(null);   // null | { mode, item? }
  const [scanner,  setScanner]  = useState(false);
  const [adjusting,setAdjusting]= useState({});
  const [err,      setErr]      = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const data = await getInventory();
      setItems(data);
    } catch (e) {
      setErr(e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    total:    items.length,
    value:    items.reduce((s, i) => s + i.quantity * i.cost_price, 0),
    low:      items.filter((i) => statusOf(i) === 'low').length,
    out:      items.filter((i) => statusOf(i) === 'out').length,
    expiring: items.filter((i) => ['expiring', 'expired'].includes(statusOf(i))).length,
    slow:     items.filter((i) => statusOf(i) === 'slow').length,
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filtered = items.filter((item) => {
    if (filter !== 'all') {
      const s = statusOf(item);
      if (filter === 'out'      && s !== 'out')     return false;
      if (filter === 'low'      && s !== 'low')     return false;
      if (filter === 'expiring' && !['expiring','expired'].includes(s)) return false;
      if (filter === 'slow'     && s !== 'slow')    return false;
    }
    if (search) {
      const q = search.toLowerCase();
      return item.name.toLowerCase().includes(q) ||
             item.sku.toLowerCase().includes(q) ||
             item.category.toLowerCase().includes(q);
    }
    return true;
  });

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAdd = async (data) => {
    const created = await addInventoryItem(data);
    setItems((p) => [...p, created].sort((a, b) => a.id - b.id));
  };

  const handleEdit = async (id, data) => {
    const updated = await updateInventoryItem(id, data);
    setItems((p) => p.map((i) => (i.id === id ? updated : i)));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item from inventory?')) return;
    await deleteInventoryItem(id);
    setItems((p) => p.filter((i) => i.id !== id));
  };

  const adjustQty = async (item, delta) => {
    const newQty = Math.max(0, item.quantity + delta);
    setAdjusting((p) => ({ ...p, [item.id]: true }));
    try {
      const updated = await updateInventoryItem(item.id, { quantity: newQty });
      setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
    } finally {
      setAdjusting((p) => ({ ...p, [item.id]: false }));
    }
  };

  // ── Scan handler — find matching SKU or open add modal ─────────────────────
  const handleScan = (barcode) => {
    setScanner(false);
    const match = items.find((i) => i.sku === barcode);
    if (match) {
      setModal({ mode: 'edit', item: { ...match } });
    } else {
      setModal({ mode: 'add', item: { ...EMPTY_FORM, sku: barcode } });
    }
  };

  // ── CSV export ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['ID','Name','SKU','Category','Quantity','Unit','Min Stock','Cost Price','Selling Price','Expiry Date','Last Restocked','Status','Notes'];
    const rows = items.map((i) => [
      i.id, i.name, i.sku, i.category, i.quantity, i.unit, i.min_stock,
      i.cost_price, i.selling_price, i.expiry_date,
      i.last_restocked ? new Date(i.last_restocked).toLocaleDateString() : '',
      STATUS_META[statusOf(i)].label, i.notes,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`));
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inventory_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading">Loading inventory…</div>;

  return (
    <div style={{ padding: '0 0 80px' }}>

      {/* Modals */}
      {scanner && <BarcodeScanner onScan={handleScan} onClose={() => setScanner(false)} />}
      {modal?.mode === 'add' && (
        <ItemModal initial={modal.item} onSave={handleAdd} onClose={() => setModal(null)} />
      )}
      {modal?.mode === 'edit' && (
        <ItemModal
          initial={modal.item}
          onSave={(data) => handleEdit(modal.item.id, data)}
          onClose={() => setModal(null)}
        />
      )}

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', margin: '12px 0' }}>{err}</div>}

      {/* ── Top action bar ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setModal({ mode: 'add' })}
          style={{ flex: 1, minWidth: 120, padding: '10px 16px', background: '#FF6B35', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Add Item
        </button>
        <button onClick={() => setScanner(true)}
          style={{ flex: 1, minWidth: 120, padding: '10px 16px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          📷 Scan Barcode
        </button>
        <button onClick={exportCSV}
          style={{ padding: '10px 14px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* ── Stats cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(100px,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Total SKUs',    value: stats.total,                       color: '#3b82f6', icon: '📦' },
          { label: 'Stock Value',   value: `₹${stats.value.toLocaleString('en-IN', {maximumFractionDigits:0})}`, color: '#10b981', icon: '💰' },
          { label: 'Low Stock',     value: stats.low,                         color: '#f97316', icon: '🟠' },
          { label: 'Out of Stock',  value: stats.out,                         color: '#ef4444', icon: '🔴' },
          { label: 'Expiring',      value: stats.expiring,                    color: '#eab308', icon: '⏰' },
          { label: 'Slow Moving',   value: stats.slow,                        color: '#64748b', icon: '⚪' },
        ].map(({ label, value, color, icon }) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 18 }}>{icon}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter tabs ── */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
        {[
          ['all',      'All Items',    stats.total],
          ['out',      '🔴 Out',       stats.out],
          ['low',      '🟠 Low Stock', stats.low],
          ['expiring', '🟡 Expiring',  stats.expiring],
          ['slow',     '⚪ Slow',      stats.slow],
        ].map(([key, label, count]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{
              whiteSpace: 'nowrap', padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: `2px solid ${filter === key ? '#FF6B35' : '#e5e7eb'}`,
              background: filter === key ? '#FF6B35' : '#fff',
              color: filter === key ? '#fff' : '#555', cursor: 'pointer',
            }}>
            {label} {count > 0 && <span style={{ opacity: 0.8 }}>({count})</span>}
          </button>
        ))}
      </div>

      {/* ── Search ── */}
      <input
        placeholder="Search by name, barcode, or category…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 10,
          fontSize: 14, outline: 'none', background: '#f9fafb', boxSizing: 'border-box', marginBottom: 14,
        }}
      />

      {/* ── Items list ── */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14 }}>
          {search ? `No items matching "${search}"` : 'No items in this category. Add your first item above.'}
        </div>
      ) : (
        filtered.map((item) => {
          const s    = statusOf(item);
          const meta = STATUS_META[s];
          const busy = adjusting[item.id];
          const margin = item.selling_price > 0 && item.cost_price > 0
            ? ((item.selling_price - item.cost_price) / item.cost_price * 100).toFixed(0)
            : null;
          const daysToExpiry = item.expiry_date
            ? Math.ceil((new Date(item.expiry_date) - Date.now()) / 864e5)
            : null;

          return (
            <div key={item.id} style={{
              background: '#fff', border: `1px solid ${s !== 'ok' ? meta.color + '55' : '#e5e7eb'}`,
              borderLeft: `4px solid ${meta.color}`, borderRadius: 12, padding: '12px 14px',
              marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              {/* Row 1: Status badge + name + actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: meta.bg, color: meta.color, whiteSpace: 'nowrap',
                    }}>{meta.dot} {meta.label}</span>
                    {item.sku && (
                      <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>#{item.sku}</span>
                    )}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4, color: '#1e293b' }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{item.category}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => setModal({ mode: 'edit', item: { ...item, expiry_date: item.expiry_date || '' } })}
                    style={{ padding: '5px 10px', fontSize: 12, background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600, color: '#475569' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(item.id)}
                    style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid #fca5a5', borderRadius: 7, cursor: 'pointer', color: '#ef4444', fontWeight: 600 }}>
                    ✕
                  </button>
                </div>
              </div>

              {/* Row 2: Qty controls + prices */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                {/* Qty adjuster */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 8px' }}>
                  <button onClick={() => adjustQty(item, -1)} disabled={busy || item.quantity <= 0}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 6, background: '#e2e8f0', cursor: 'pointer', fontSize: 16, fontWeight: 700, lineHeight: 1 }}>−</button>
                  <span style={{ minWidth: 40, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>
                    {item.quantity % 1 === 0 ? item.quantity : item.quantity.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{item.unit}</span>
                  <button onClick={() => adjustQty(item, 1)} disabled={busy}
                    style={{ width: 26, height: 26, border: 'none', borderRadius: 6, background: '#FF6B35', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 700, lineHeight: 1 }}>+</button>
                </div>

                {/* Min stock indicator */}
                {item.min_stock > 0 && (
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    Min: {item.min_stock} {item.unit}
                  </span>
                )}

                {/* Prices */}
                {item.cost_price > 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>Cost ₹{item.cost_price}</span>
                )}
                {item.selling_price > 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>MRP ₹{item.selling_price}</span>
                )}
                {margin !== null && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>+{margin}% margin</span>
                )}
              </div>

              {/* Expiry warning */}
              {daysToExpiry !== null && (
                <div style={{ marginTop: 8, fontSize: 12, color: daysToExpiry < 0 ? '#7c3aed' : daysToExpiry <= 7 ? '#d97706' : '#64748b', fontWeight: 600 }}>
                  {daysToExpiry < 0 ? `⚠️ Expired ${Math.abs(daysToExpiry)} days ago` :
                   daysToExpiry === 0 ? '⚠️ Expires today!' :
                   daysToExpiry <= 7 ? `⏰ Expires in ${daysToExpiry} day${daysToExpiry > 1 ? 's' : ''}` :
                   `📅 Expires ${new Date(item.expiry_date).toLocaleDateString('en-IN')}`}
                </div>
              )}

              {/* Notes */}
              {item.notes && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>📝 {item.notes}</div>
              )}

              {/* Slow moving warning */}
              {s === 'slow' && item.last_restocked && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                  ⚪ Last restocked {Math.floor((Date.now() - new Date(item.last_restocked)) / 864e5)} days ago
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
